import React, { useState, useRef, useMemo, useCallback, forwardRef, useImperativeHandle } from 'react';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { Dialog } from 'primereact/dialog';

// ─── Types ────────────────────────────────────────────────────────────────────
// Mirrors the Go backend exactly: adminControllers.TeacherController /
// BulkUploadTeachersFromCSV, mounted at POST /teachers/bulk-csv by
// RegisterTeacherRoutes (superadmin, admin, principal only).
//
// IMPORTANT — this backend is validate-ALL-THEN-insert-ALL, not per-row like
// the student importer:
//   • If ANY row fails validation, NOTHING is inserted and the response is
//     { status: "error", validation_results: { valid_count, invalid_count, errors[] } }
//   • If EVERY row passes validation, ALL rows are inserted transactionally
//     and the response is
//     { status: "success", process_results: { success, failed, successful_teachers[] } }
// There is no partial/mixed per-row success+failure result — the UI below
// reflects that instead of trying to force a shared row-status list.

interface BulkCSVValidationErrorEntry {
  row: number;
  message: string;
  details?: string[];
  data?: { name?: string; email?: string };
}

interface BulkCSVValidationResults {
  valid_count: number;
  invalid_count: number;
  errors: BulkCSVValidationErrorEntry[];
}

interface BulkCSVSuccessfulTeacher {
  name: string;
  email: string;
  teacher_id: string;
  sp_teacher_id?: string;
  employee_id?: string;
  user_id: string;
  username: string;
}

interface BulkCSVProcessResults {
  success: number;
  failed: number;
  successful_teachers: BulkCSVSuccessfulTeacher[];
}

interface BulkCSVResponse {
  status: 'success' | 'error';
  message: string;
  validation_results?: BulkCSVValidationResults;
  process_results?: BulkCSVProcessResults;
}

interface ApiClient {
  post: (url: string, data: any, config?: any) => Promise<{ data: BulkCSVResponse }>;
}

interface BulkRegisterTeachersCSVProps {
  apiClient: ApiClient;
  /** Optional — backend falls back to context/token resolution (ResolveSchoolContext) when omitted. */
  schoolId?: string | null;
  /** Called once the upload finishes with at least one successful insert. */
  onSuccess?: (results: BulkCSVProcessResults) => void;
  label?: string;
  disabled?: boolean;
  hideTrigger?: boolean;
}

export interface BulkRegisterTeachersCSVHandle {
  open: () => void;
}

type Stage = 'upload' | 'uploading' | 'results';

// ─── CSV column contract ──────────────────────────────────────────────────────
// Order mirrors the Go controller's requiredFields slice, required columns
// first, then the optional bank-detail trio. Headers are matched by name
// (lower-cased, trimmed) on the backend, not by position, but keeping this
// order makes an exported/re-imported roster predictable.

const TEMPLATE_HEADERS = [
  'name', 'qualification', 'gender', 'marital_status', 'email_id',
  'parentage', 'dob', 'address', 'contact_no', 'user_login', 'user_pass',
  'account_no', 'bank_name', 'ifsc_code',
];

const REQUIRED_HEADERS = new Set([
  'name', 'qualification', 'gender', 'marital_status', 'email_id',
  'parentage', 'dob', 'address', 'contact_no', 'user_login', 'user_pass',
]);

const TEMPLATE_EXAMPLE_ROW = [
  'Aarav Sharma', 'M.Sc, B.Ed', 'Male', 'Married', 'aarav.sharma@example.com',
  'Rakesh Sharma', '1992-06-21', '221B, MG Road, Pune', '9876500001', 'default', 'default',
  '', '', '',
];

function buildTemplateCSV(): string {
  const esc = (v: string) => `"${v.replace(/"/g, '""')}"`;
  return [
    TEMPLATE_HEADERS.map(esc).join(','),
    TEMPLATE_EXAMPLE_ROW.map(esc).join(','),
  ].join('\r\n');
}

// ─── Lightweight CSV parsing (client-side preview only — server is the source
// of truth for validation). Handles quoted fields and escaped quotes. ────────

function parseCSV(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let field = '';
  let inQuotes = false;

  for (let i = 0; i < text.length; i++) {
    const c = text[i];
    if (inQuotes) {
      if (c === '"') {
        if (text[i + 1] === '"') { field += '"'; i++; }
        else inQuotes = false;
      } else {
        field += c;
      }
    } else if (c === '"') {
      inQuotes = true;
    } else if (c === ',') {
      row.push(field); field = '';
    } else if (c === '\r') {
      // skip, \n handles the break
    } else if (c === '\n') {
      row.push(field); field = '';
      rows.push(row); row = [];
    } else {
      field += c;
    }
  }
  if (field.length > 0 || row.length > 0) { row.push(field); rows.push(row); }
  return rows.filter((r) => r.length > 1 || (r[0] ?? '').trim() !== '');
}

function downloadTextFile(filename: string, content: string, mime = 'text/csv;charset=utf-8'): void {
  const blob = new Blob(['\ufeff' + content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 5000);
}

// ─── Sub-components (visual language matches BulkRegisterStudentsCSV) ────────

const SectionHeader: React.FC<{ icon: string; title: string }> = ({ icon, title }) => (
  <div style={{
    display: 'flex', alignItems: 'center', gap: 10,
    marginBottom: 16, paddingBottom: 12,
    borderBottom: '1px solid rgba(229,231,235,0.6)',
  }}>
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      width: 32, height: 32, borderRadius: 8,
      background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
      color: 'white', flexShrink: 0,
    }}>
      <i className={icon} style={{ fontSize: 15 }} />
    </div>
    <span style={{
      color: '#1e3a8a', fontWeight: 700, fontSize: 14,
      letterSpacing: '0.02em', textTransform: 'uppercase',
    }}>
      {title}
    </span>
  </div>
);

const Card: React.FC<{ children: React.ReactNode; style?: React.CSSProperties }> = ({ children, style }) => (
  <div style={{
    background: 'rgba(255,255,255,0.8)', borderRadius: 16, padding: 20,
    border: '1px solid rgba(59,130,246,0.1)',
    boxShadow: '0 4px 16px rgba(0,0,0,0.05)',
    ...style,
  }}>
    {children}
  </div>
);

// ─── Main component ───────────────────────────────────────────────────────────

const BulkRegisterTeachersCSV = forwardRef<BulkRegisterTeachersCSVHandle, BulkRegisterTeachersCSVProps>(({
  apiClient,
  schoolId,
  onSuccess,
  label = 'Bulk Register Teachers',
  disabled = false,
  hideTrigger = false,
}, ref) => {
  const toast = useRef<Toast>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [visible, setVisible] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [stage, setStage] = useState<Stage>('upload');
  const [dragActive, setDragActive] = useState(false);

  const [file, setFile] = useState<File | null>(null);
  const [previewRows, setPreviewRows] = useState<string[][] | null>(null);
  const [headerWarnings, setHeaderWarnings] = useState<string[]>([]);
  const [parseError, setParseError] = useState<string | null>(null);

  const [progress, setProgress] = useState(0);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [response, setResponse] = useState<BulkCSVResponse | null>(null);

  const reset = () => {
    setFile(null); setPreviewRows(null); setHeaderWarnings([]); setParseError(null);
    setProgress(0); setUploadError(null); setResponse(null);
    setStage('upload');
  };

  const handleOpen = () => { reset(); setVisible(true); };

  useImperativeHandle(ref, () => ({ open: handleOpen }));

  const handleClose = () => {
    if (stage === 'uploading') return;
    setIsClosing(true);
    setTimeout(() => {
      setVisible(false); setIsClosing(false);
      if (response?.status === 'success' && (response.process_results?.success ?? 0) > 0) {
        onSuccess?.(response.process_results!);
      }
      reset();
    }, 300);
  };

  const readFile = useCallback((f: File) => {
    setParseError(null);
    setHeaderWarnings([]);
    setPreviewRows(null);

    if (!f.name.toLowerCase().endsWith('.csv')) {
      setParseError('Only .csv files are accepted.');
      setFile(null);
      return;
    }

    setFile(f);
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const text = String(reader.result ?? '');
        const rows = parseCSV(text);
        if (rows.length === 0) {
          setParseError('The file appears to be empty.');
          return;
        }
        const header = rows[0].map((h) => h.trim().toLowerCase());
        const missing = TEMPLATE_HEADERS.filter((h) => REQUIRED_HEADERS.has(h) && !header.includes(h));
        setHeaderWarnings(missing);
        setPreviewRows(rows);
      } catch {
        setParseError('Could not read this file as CSV.');
      }
    };
    reader.onerror = () => setParseError('Could not read this file.');
    reader.readAsText(f);
  }, []);

  const dataRowCount = previewRows ? Math.max(0, previewRows.length - 1) : 0;

  const onFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (f) readFile(f);
    e.target.value = '';
  };

  const onDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault(); setDragActive(false);
    const f = e.dataTransfer.files?.[0];
    if (f) readFile(f);
  };

  const handleUpload = async () => {
    if (!file) return;
    setStage('uploading');
    setProgress(0);
    setUploadError(null);

    const formData = new FormData();
    if (schoolId) formData.append('school_id', schoolId);
    formData.append('csv_file', file);

    try {
      const res = await apiClient.post('/teachers/bulk-csv', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (evt: any) => {
          if (evt.total) setProgress(Math.round((evt.loaded / evt.total) * 100));
        },
      });
      setResponse(res.data);
      setStage('results');

      if (res.data.status === 'success') {
        const { success = 0, failed = 0 } = res.data.process_results ?? {};
        toast.current?.show({
          severity: failed === 0 ? 'success' : 'warn',
          summary: 'Import Complete',
          detail: res.data.message,
          life: 6000,
        });
      } else {
        toast.current?.show({
          severity: 'error',
          summary: 'Validation Failed — Nothing Was Imported',
          detail: res.data.message,
          life: 6000,
        });
      }
    } catch (err: any) {
      // Axios rejects on any non-2xx response — and the Go backend returns
      // HTTP 400 both for row-validation failures (with a validation_results
      // payload) and for hard request errors (bad file, missing columns,
      // etc). Row-validation failures still deserve the full results screen,
      // not just a generic toast, so detect that shape here and route it
      // the same way a 200 "status: error" response would be routed above.
      const errData = err?.response?.data;
      // eslint-disable-next-line no-console
      console.log('bulk-csv upload error response:', errData);

      if (errData?.validation_results) {
        setResponse(errData as BulkCSVResponse);
        setStage('results');
        toast.current?.show({
          severity: 'error',
          summary: 'Validation Failed — Nothing Was Imported',
          detail: errData.message,
          life: 6000,
        });
        return;
      }

      const msg = errData?.message || err?.message || 'Upload failed. Please try again.';
      setUploadError(msg);
      setStage('upload');
      toast.current?.show({ severity: 'error', summary: 'Upload Failed', detail: msg, life: 6000 });
    }
  };

  const validationErrors = useMemo(
    () => response?.status === 'error' ? (response.validation_results?.errors ?? []) : [],
    [response]
  );

  const downloadErrorReport = () => {
    if (validationErrors.length === 0) return;
    const esc = (v: string | number | undefined) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const csv = [
      ['row', 'name', 'email', 'message', 'details'].map(esc).join(','),
      ...validationErrors.map((e) => [
        e.row, e.data?.name ?? '', e.data?.email ?? '', e.message, (e.details ?? []).join(' | '),
      ].map(esc).join(',')),
    ].join('\r\n');
    downloadTextFile(`bulk_teacher_validation_errors_${new Date().toISOString().slice(0, 10)}.csv`, csv);
  };

  const canSubmit = !!file && !parseError && dataRowCount > 0;

  const headerTemplate = () => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '8px 0' }}>
      <div style={{
        width: 54, height: 54, borderRadius: 14,
        background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: 'white', fontSize: 24, fontWeight: 700,
        boxShadow: '0 8px 24px rgba(59,130,246,0.3)',
        flexShrink: 0, border: '3px solid white',
      }}>
        <i className="pi pi-user-plus" />
      </div>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 20, fontWeight: 700, color: '#1e40af', lineHeight: 1.2, marginBottom: 4 }}>
          Bulk Register Teachers
        </div>
        <div style={{ fontSize: 13, color: '#64748b', fontWeight: 500 }}>
          Upload a CSV to create many teacher accounts at once
        </div>
      </div>
    </div>
  );

  return (
    <>
      <Toast ref={toast} position="top-right" />

      {!hideTrigger && (
        <Button
          icon="pi pi-upload"
          label={label}
          onClick={handleOpen}
          disabled={disabled}
          style={{
            background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
            border: 'none', borderRadius: 10, padding: '9px 18px',
            fontSize: 13, fontWeight: 600, color: 'white', gap: 6,
            boxShadow: '0 4px 14px rgba(59,130,246,0.35)',
          }}
        />
      )}

      <Dialog
        visible={visible && !isClosing}
        onHide={handleClose}
        header={headerTemplate}
        closable={stage !== 'uploading'}
        style={{ width: '90vw', maxWidth: 640, borderRadius: 24, overflow: 'hidden' }}
        contentStyle={{
          padding: 18,
          background: 'linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%)',
          maxHeight: 'calc(90vh - 120px)', overflowY: 'auto',
          border: '12px solid rgba(255,255,255,0.8)',
          borderRadius: '0 0 24px 24px', borderTop: 'none',
        }}
        headerStyle={{
          background: 'white', borderBottom: '1px solid #e5e7eb',
          padding: '20px 24px', borderRadius: '24px 24px 0 0',
          borderLeft: '12px solid rgba(255,255,255,0.8)',
          borderRight: '12px solid rgba(255,255,255,0.8)',
          borderTop: '12px solid rgba(255,255,255,0.8)',
          margin: '-12px -12px 0 -12px',
        }}
        modal dismissableMask={stage !== 'uploading'} draggable={false} resizable={false} blockScroll
      >
        <style>{`
          .p-dialog-mask { backdrop-filter: blur(4px); background-color: rgba(0,0,0,0.25) !important; }
          .brt-dropzone { transition: all 0.18s ease; }
          .brt-dropzone.active { border-color: #3b82f6 !important; background: rgba(59,130,246,0.06) !important; }
          .brt-cancel-btn:hover { border-color:#3b82f6 !important; color:#1e40af !important; }
          .brt-submit-btn:hover:not(:disabled) { box-shadow: 0 6px 20px rgba(59,130,246,0.45) !important; transform: translateY(-1px); }
          .brt-result-row:hover { background: rgba(59,130,246,0.03) !important; }
        `}</style>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

          {stage === 'upload' && (
            <>
              <Card>
                <SectionHeader icon="pi pi-file-import" title="CSV Template" />
                <div style={{ fontSize: 13, color: '#4b5563', fontWeight: 500, marginBottom: 12, lineHeight: 1.6 }}>
                  Required columns: <code style={{ fontFamily: 'monospace', fontSize: 11.5 }}>
                    name, qualification, gender, marital_status, email_id, parentage, dob,
                    address, contact_no, user_login, user_pass
                  </code>. Optional: <code style={{ fontFamily: 'monospace', fontSize: 11.5 }}>
                    account_no, bank_name, ifsc_code
                  </code>.
                  <br /><br />
                  Set both <code style={{ fontFamily: 'monospace' }}>user_login</code> and{' '}
                  <code style={{ fontFamily: 'monospace' }}>user_pass</code> to the literal word{' '}
                  <code style={{ fontFamily: 'monospace' }}>default</code> to auto-generate credentials from the
                  name and DOB (requires a valid DOB). If you set a password yourself, it must be at least 8
                  characters with one uppercase letter and one number. Gender must be exactly{' '}
                  <code style={{ fontFamily: 'monospace' }}>Male</code>, <code style={{ fontFamily: 'monospace' }}>Female</code>,
                  or <code style={{ fontFamily: 'monospace' }}>Other</code>. DOB accepts{' '}
                  <code style={{ fontFamily: 'monospace' }}>YYYY-MM-DD</code> or <code style={{ fontFamily: 'monospace' }}>DD-MM-YYYY</code>.
                  <br /><br />
                  <strong>Note:</strong> every row in the file is validated before anything is saved — if even one
                  row fails, no teachers are created and you'll get a full list of what to fix.
                </div>
                <button
                  onClick={() => downloadTextFile('teacher_bulk_register_template.csv', buildTemplateCSV())}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 8,
                    padding: '9px 16px', borderRadius: 10, border: '1.5px solid #bfdbfe',
                    background: 'rgba(219,234,254,0.4)', color: '#1e40af',
                    fontSize: 13, fontWeight: 600, cursor: 'pointer',
                  }}
                >
                  <i className="pi pi-download" style={{ fontSize: 12 }} /> Download CSV Template
                </button>
              </Card>

              <Card>
                <SectionHeader icon="pi pi-cloud-upload" title="Upload File" />

                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv"
                  onChange={onFileInputChange}
                  style={{ display: 'none' }}
                />

                <div
                  className={`brt-dropzone${dragActive ? ' active' : ''}`}
                  onClick={() => fileInputRef.current?.click()}
                  onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
                  onDragLeave={() => setDragActive(false)}
                  onDrop={onDrop}
                  style={{
                    border: '2px dashed #cbd5e1', borderRadius: 14, padding: '28px 20px',
                    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8,
                    cursor: 'pointer', background: 'rgba(248,250,252,0.6)', textAlign: 'center',
                  }}
                >
                  <div style={{
                    width: 44, height: 44, borderRadius: 12,
                    background: 'linear-gradient(135deg, rgba(59,130,246,0.12), rgba(59,130,246,0.06))',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <i className="pi pi-file" style={{ fontSize: 20, color: '#3b82f6' }} />
                  </div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#1f2937' }}>
                    {file ? file.name : 'Drop a CSV file here, or click to browse'}
                  </div>
                  <div style={{ fontSize: 11, color: '#9ca3af', fontWeight: 500 }}>.csv files only</div>
                </div>

                {parseError && (
                  <div style={{
                    marginTop: 12, display: 'flex', alignItems: 'center', gap: 8,
                    padding: '9px 13px', borderRadius: 8, background: '#fee2e2',
                    border: '1px solid #fca5a5', fontSize: 12, color: '#991b1b', fontWeight: 600,
                  }}>
                    <i className="pi pi-exclamation-triangle" /> {parseError}
                  </div>
                )}

                {uploadError && (
                  <div style={{
                    marginTop: 12, display: 'flex', alignItems: 'center', gap: 8,
                    padding: '9px 13px', borderRadius: 8, background: '#fee2e2',
                    border: '1px solid #fca5a5', fontSize: 12, color: '#991b1b', fontWeight: 600,
                  }}>
                    <i className="pi pi-exclamation-triangle" /> {uploadError}
                  </div>
                )}

                {previewRows && !parseError && (
                  <div style={{ marginTop: 14 }}>
                    <div style={{
                      display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10,
                      fontSize: 12, color: '#059669', fontWeight: 600,
                    }}>
                      <i className="pi pi-check-circle" />
                      {dataRowCount} teacher row{dataRowCount !== 1 ? 's' : ''} detected
                    </div>

                    {headerWarnings.length > 0 && (
                      <div style={{
                        marginBottom: 10, padding: '9px 13px', borderRadius: 8,
                        background: '#fef3c7', border: '1px solid #fcd34d',
                        fontSize: 12, color: '#92400e', fontWeight: 600,
                      }}>
                        <i className="pi pi-exclamation-triangle" style={{ marginRight: 6 }} />
                        Missing expected column{headerWarnings.length !== 1 ? 's' : ''}: {headerWarnings.join(', ')}.
                        The server will reject the whole file if required columns are missing.
                      </div>
                    )}

                    <div style={{ overflowX: 'auto', borderRadius: 10, border: '1px solid #e5e7eb' }}>
                      <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 11.5 }}>
                        <thead>
                          <tr>
                            {previewRows[0].map((h, i) => (
                              <th key={i} style={{
                                textAlign: 'left', padding: '7px 10px', background: '#f9fafb',
                                color: '#6b7280', fontWeight: 700, whiteSpace: 'nowrap',
                                borderBottom: '1px solid #e5e7eb',
                              }}>{h.trim() || `col_${i + 1}`}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {previewRows.slice(1, 6).map((r, ri) => (
                            <tr key={ri}>
                              {r.map((cell, ci) => (
                                <td key={ci} style={{
                                  padding: '7px 10px', whiteSpace: 'nowrap',
                                  borderBottom: '1px solid #f3f4f6', color: '#374151',
                                }}>{cell || '—'}</td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    {dataRowCount > 5 && (
                      <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 6, fontWeight: 500 }}>
                        Showing first 5 of {dataRowCount} rows.
                      </div>
                    )}
                  </div>
                )}
              </Card>
            </>
          )}

          {stage === 'uploading' && (
            <Card>
              <SectionHeader icon="pi pi-sync" title="Uploading & Validating" />
              <div style={{ height: 8, borderRadius: 99, background: '#e5e7eb', overflow: 'hidden', marginBottom: 10 }}>
                <div style={{
                  height: '100%', borderRadius: 99, width: `${progress}%`,
                  background: 'linear-gradient(90deg, #3b82f6, #60a5fa)', transition: 'width 0.3s ease',
                }} />
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: '#6b7280', fontWeight: 500 }}>
                <span>{progress < 100 ? 'Uploading file…' : 'Validating every row before saving anything…'}</span>
                <span style={{ color: '#1e40af', fontWeight: 700 }}>{progress}%</span>
              </div>
            </Card>
          )}

          {stage === 'results' && response?.status === 'error' && (
            <>
              <Card>
                <SectionHeader icon="pi pi-exclamation-triangle" title="Validation Failed — Nothing Imported" />
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                  <div style={{ padding: '12px 10px', borderRadius: 10, background: 'rgba(220,252,231,0.6)', border: '1px solid #86efac', textAlign: 'center' }}>
                    <div style={{ fontSize: 20, fontWeight: 800, color: '#166534' }}>
                      {response.validation_results?.valid_count ?? 0}
                    </div>
                    <div style={{ fontSize: 11, color: '#166534', fontWeight: 600, marginTop: 2 }}>Valid Rows</div>
                  </div>
                  <div style={{ padding: '12px 10px', borderRadius: 10, background: 'rgba(254,226,226,0.7)', border: '1px solid #fca5a5', textAlign: 'center' }}>
                    <div style={{ fontSize: 20, fontWeight: 800, color: '#991b1b' }}>
                      {response.validation_results?.invalid_count ?? 0}
                    </div>
                    <div style={{ fontSize: 11, color: '#991b1b', fontWeight: 600, marginTop: 2 }}>Invalid Rows</div>
                  </div>
                </div>
                <div style={{
                  marginTop: 12, padding: '9px 13px', borderRadius: 8, background: '#fef3c7',
                  border: '1px solid #fcd34d', fontSize: 12, color: '#92400e', fontWeight: 600,
                }}>
                  <i className="pi pi-info-circle" style={{ marginRight: 6 }} />
                  Fix the rows below and re-upload the whole file — even the valid rows must be re-submitted.
                </div>
              </Card>

              <Card>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4, flexWrap: 'wrap', gap: 10 }}>
                  <SectionHeader icon="pi pi-list" title="Row Errors" />
                </div>
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: -10, marginBottom: 10 }}>
                  <button
                    onClick={downloadErrorReport}
                    style={{
                      padding: '6px 13px', borderRadius: 99, fontSize: 12, fontWeight: 700,
                      cursor: 'pointer', border: '1.5px solid #fca5a5', background: 'white', color: '#991b1b',
                      display: 'inline-flex', alignItems: 'center', gap: 6,
                    }}
                  >
                    <i className="pi pi-download" style={{ fontSize: 11 }} /> Error Report
                  </button>
                </div>

                <div style={{ maxHeight: 320, overflowY: 'auto', borderRadius: 10, border: '1px solid #e5e7eb' }}>
                  {validationErrors.length === 0 && (
                    <div style={{ padding: '20px 12px', textAlign: 'center', fontSize: 12, color: '#9ca3af', fontWeight: 500 }}>
                      No error detail available.
                    </div>
                  )}
                  {validationErrors.map((e, i) => (
                    <div key={i} className="brt-result-row" style={{
                      display: 'flex', alignItems: 'flex-start', gap: 10,
                      padding: '10px 12px', borderBottom: '1px solid #f3f4f6',
                    }}>
                      <div style={{
                        width: 22, height: 22, borderRadius: '50%', flexShrink: 0, marginTop: 1,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        background: '#fee2e2', color: '#991b1b',
                      }}>
                        <i className="pi pi-times" style={{ fontSize: 10 }} />
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                          <span style={{ fontSize: 12, fontWeight: 700, color: '#1f2937' }}>Row {e.row}</span>
                          {e.data?.name && <span style={{ fontSize: 12, color: '#6b7280', fontWeight: 500 }}>{e.data.name}</span>}
                          {e.data?.email && <span style={{ fontSize: 11, color: '#9ca3af', fontFamily: 'monospace' }}>{e.data.email}</span>}
                        </div>
                        <div style={{ fontSize: 12, color: '#991b1b', fontWeight: 500, marginTop: 2 }}>{e.message}</div>
                        {e.details && e.details.length > 0 && (
                          <ul style={{ margin: '6px 0 0', paddingLeft: 18, fontSize: 11.5, color: '#7f1d1d' }}>
                            {e.details.map((d, di) => <li key={di}>{d}</li>)}
                          </ul>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            </>
          )}

          {stage === 'results' && response?.status === 'success' && (
            <>
              <Card>
                <SectionHeader icon="pi pi-check-circle" title="Import Summary" />
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                  <div style={{ padding: '12px 10px', borderRadius: 10, background: 'rgba(220,252,231,0.6)', border: '1px solid #86efac', textAlign: 'center' }}>
                    <div style={{ fontSize: 20, fontWeight: 800, color: '#166534' }}>
                      {response.process_results?.success ?? 0}
                    </div>
                    <div style={{ fontSize: 11, color: '#166534', fontWeight: 600, marginTop: 2 }}>Teachers Created</div>
                  </div>
                  <div style={{
                    padding: '12px 10px', borderRadius: 10,
                    background: (response.process_results?.failed ?? 0) > 0 ? 'rgba(254,226,226,0.7)' : '#f8fafc',
                    border: `1px solid ${(response.process_results?.failed ?? 0) > 0 ? '#fca5a5' : '#e5e7eb'}`, textAlign: 'center',
                  }}>
                    <div style={{ fontSize: 20, fontWeight: 800, color: (response.process_results?.failed ?? 0) > 0 ? '#991b1b' : '#9ca3af' }}>
                      {response.process_results?.failed ?? 0}
                    </div>
                    <div style={{ fontSize: 11, color: (response.process_results?.failed ?? 0) > 0 ? '#991b1b' : '#9ca3af', fontWeight: 600, marginTop: 2 }}>Failed</div>
                  </div>
                </div>
              </Card>

              <Card>
                <SectionHeader icon="pi pi-list" title="Created Teachers" />
                <div style={{ maxHeight: 320, overflowY: 'auto', borderRadius: 10, border: '1px solid #e5e7eb' }}>
                  {(response.process_results?.successful_teachers ?? []).length === 0 && (
                    <div style={{ padding: '20px 12px', textAlign: 'center', fontSize: 12, color: '#9ca3af', fontWeight: 500 }}>
                      No records to show.
                    </div>
                  )}
                  {(response.process_results?.successful_teachers ?? []).map((t, i) => (
                    <div key={i} className="brt-result-row" style={{
                      display: 'flex', alignItems: 'flex-start', gap: 10,
                      padding: '10px 12px', borderBottom: '1px solid #f3f4f6',
                    }}>
                      <div style={{
                        width: 22, height: 22, borderRadius: '50%', flexShrink: 0, marginTop: 1,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        background: '#dcfce7', color: '#166534',
                      }}>
                        <i className="pi pi-check" style={{ fontSize: 10 }} />
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                          <span style={{ fontSize: 12, fontWeight: 700, color: '#1f2937' }}>{t.name}</span>
                          <span style={{ fontSize: 11, color: '#9ca3af', fontFamily: 'monospace' }}>{t.email}</span>
                          {t.employee_id && (
                            <span style={{ fontSize: 10, padding: '1px 7px', borderRadius: 99, background: '#f1f5f9', color: '#475569', fontWeight: 600 }}>
                              ID {t.employee_id}
                            </span>
                          )}
                        </div>
                        <div style={{ fontSize: 12, color: '#166534', fontWeight: 500, marginTop: 2 }}>
                          username: <span style={{ fontFamily: 'monospace' }}>{t.username}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            </>
          )}

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, borderTop: '1px solid #e5e7eb', paddingTop: 16, marginTop: 4 }}>
            <button
              className="brt-cancel-btn"
              style={{
                padding: '9px 20px', borderRadius: 10, border: '1.5px solid #e5e7eb',
                background: 'white', fontSize: 13, fontWeight: 600, color: '#6b7280',
                cursor: stage === 'uploading' ? 'not-allowed' : 'pointer',
              }}
              onClick={handleClose}
              disabled={stage === 'uploading'}
            >
              {stage === 'results' ? 'Close' : 'Cancel'}
            </button>

            {stage === 'upload' && (
              <button
                className="brt-submit-btn"
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 7,
                  padding: '9px 22px', borderRadius: 10, border: 'none',
                  background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
                  fontSize: 13, fontWeight: 700, color: 'white',
                  boxShadow: '0 4px 14px rgba(59,130,246,0.35)',
                  cursor: canSubmit ? 'pointer' : 'not-allowed',
                  opacity: canSubmit ? 1 : 0.6,
                }}
                onClick={handleUpload}
                disabled={!canSubmit}
              >
                <i className="pi pi-upload" style={{ fontSize: 13 }} />
                Upload {dataRowCount > 0 ? `${dataRowCount} Teacher${dataRowCount !== 1 ? 's' : ''}` : 'CSV'}
              </button>
            )}

            {stage === 'results' && (
              <button
                className="brt-submit-btn"
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 7,
                  padding: '9px 22px', borderRadius: 10, border: 'none',
                  background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
                  fontSize: 13, fontWeight: 700, color: 'white',
                  boxShadow: '0 4px 14px rgba(59,130,246,0.35)', cursor: 'pointer',
                }}
                onClick={reset}
              >
                <i className="pi pi-replay" style={{ fontSize: 13 }} /> Upload Another File
              </button>
            )}
          </div>

        </div>
      </Dialog>
    </>
  );
});

BulkRegisterTeachersCSV.displayName = 'BulkRegisterTeachersCSV';

export default BulkRegisterTeachersCSV;